import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OverlayModule } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';

import { LanguageService } from '../../../shared/services/language.service';
import * as CustomLanguagesSelectors from '../../../store/custom-languages-store/custom-languages.selectors';
import { ValuesetItem } from '../../../shared/api/types/GraphQL';
import { isSameLanguage } from '../../../shared/utils/language-code.util';
import { TopbarMenuService } from '../topbar-menu.service';
import { ChromeReadyService } from '../../chrome-ready.service';
import * as AuthSelectors from '../../../auth/store/auth.selectors';

export interface LanguageRow {
  code: string;
  label: string;
  /** Present in the interface list but not the data list — see the panel note. */
  interfaceOnly: boolean;
}

/**
 * Interface + data language switcher (NBO-2331, amended scope 2026-08-26).
 *
 * Two settings from two different backend valuesets (SYSTEM_APP_LANGUAGES /
 * SYSTEM_DATA_LANGUAGES) that overlap but are NOT equal — a tenant can have IT
 * product content with no IT interface. Making that visible is the whole point,
 * so the separated state shows both lists SIDE BY SIDE; a drill-in would hide
 * exactly the fact the user needs. Linked is the default for everyone.
 *
 * The separate/unseparate control is a FOOTER row phrased as a verb in both
 * directions, reusing the notifications panel's footer treatment (same job). A
 * panel-HEADER control was drawn and rejected: it reads as a section title and
 * its label flips between describing an action and describing a state.
 */
@Component({
  selector: 'app-topbar-language-switcher',
  standalone: true,
  imports: [OverlayModule, TranslateModule, MatIconModule],
  templateUrl: './topbar-language-switcher.component.html',
  styleUrls: ['./topbar-language-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarLanguageSwitcherComponent {
  // Shared across the five clusters so only one popover is open and switching
  // between triggers takes one click (see TopbarMenuService).
  readonly open = this.menu.openFor('language');

  /** Shared with the rest of the identity cluster — one reveal (NBO-2331 r4). */
  readonly ready = inject(ChromeReadyService).identityReady;

  private readonly appLanguages = this.languageService.appLanguages;
  private readonly dataLanguages = toSignal(
    this.store.select(CustomLanguagesSelectors.selectCustomLanguages),
    { initialValue: [] as ValuesetItem[] }
  );

  // The UI language comes from the auth slice, not ngx-translate: the store is
  // what the active-row highlight has always compared against, and
  // translate.currentLang only flips once the translation asset has loaded.
  readonly uiCode = toSignal(this.store.select(AuthSelectors.selectAppLanguage), { initialValue: '' });
  readonly dataCode = toSignal(this.languageService.observeContentLanguage(), { initialValue: '' });
  readonly linked = toSignal(this.languageService.observeDataFollowsInterface(), { initialValue: true });

  private readonly dataCodes = computed(() => this.dataLanguages().map((l) => l.value));

  /** A tenant with one data language can never blank the screen — no columns, no footer, no markers. */
  readonly singleDataLanguage = computed(() => this.dataLanguages().length < 2);

  readonly separated = computed(() => !this.linked() && !this.singleDataLanguage());

  readonly interfaceRows = computed<LanguageRow[]>(() =>
    this.appLanguages().map((l) => ({
      code: l.value,
      label: this.labelOf(l),
      // Only meaningful in the LINKED list, and never on a single-data-language
      // tenant (nothing can blank there).
      interfaceOnly:
        !this.singleDataLanguage() && !this.dataCodes().some((c) => isSameLanguage(c, l.value))
    }))
  );

  readonly dataRows = computed<LanguageRow[]>(() =>
    this.dataLanguages().map((l) => ({ code: l.value, label: this.labelOf(l), interfaceOnly: false }))
  );

  /**
   * Linked, on an interface language the data list cannot serve: name what the
   * data actually resolves to rather than letting the screen go blank.
   */
  readonly strandedFallbackLabel = computed<string | null>(() => {
    if (this.separated() || this.singleDataLanguage()) {
      return null;
    }
    const ui = this.uiCode();
    if (!ui || this.dataCodes().some((c) => isSameLanguage(c, ui))) {
      return null;
    }
    const resolved =
      this.dataLanguages().find((l) => isSameLanguage(l.value, this.dataCode())) ?? this.dataLanguages()[0];
    return resolved ? this.labelOf(resolved) : null;
  });

  readonly uiLabel = computed(
    () => this.interfaceRows().find((r) => isSameLanguage(r.code, this.uiCode()))?.label ?? this.uiCode()
  );
  readonly dataLabel = computed(
    () => this.dataRows().find((r) => isSameLanguage(r.code, this.dataCode()))?.label ?? this.dataCode()
  );

  constructor(
    private menu: TopbarMenuService,
    private languageService: LanguageService,
    private store: Store
  ) {}

  toggle(): void {
    this.menu.toggle('language');
  }

  onBackdropClick(event: MouseEvent): void {
    this.menu.handleBackdropClick(event);
  }

  onDetach(): void {
    this.menu.closeIf('language');
  }

  isActive(code: string, active: string): boolean {
    return isSameLanguage(code, active);
  }

  // Picking a language does NOT close the panel (r4), matching the theme
  // segment in the user menu. This panel carries TWO settings, so closing on
  // the first pick forced a second trip through the trigger to set the other;
  // it also blocked the legitimate move of trying a few data languages to see
  // how the content reads. The labels re-render in the chosen language while
  // the panel is open, which doubles as confirmation. The trigger, the
  // backdrop and Escape all still close it.

  /** In the linked list this moves BOTH settings; separated, it moves only the interface. */
  pickInterface(code: string): void {
    this.languageService.setInterfaceLanguage(code);
  }

  /** Choosing a data language IS the act of unlinking (LanguageService handles that). */
  pickData(code: string): void {
    this.languageService.setDataLanguage(code);
  }

  toggleLink(): void {
    this.languageService.setDataFollowsInterface(this.separated());
  }

  // Meta-UI exception (design canon): a language's OWN name must render even
  // when it has no description in the active language — hence the
  // first-available fallback, which is exactly what the legacy menu did via
  // the localizedString pipe's 'first-available' mode. Do not copy elsewhere.
  private labelOf(item: ValuesetItem): string {
    const active = this.dataCode();
    const descriptions = item.descriptions ?? [];
    const direct = descriptions.find((d) => d?.language && isSameLanguage(d.language, active));
    return direct?.value || descriptions.find((d) => d?.value)?.value || item.value;
  }
}
